// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { Button } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCompare } from "./AskV2ReviewCompare";
import { AskV2ReviewCompareStatus } from "./AskV2ReviewCompareStatus";
import { type useAskPunttReview } from "./useAskPunttReview";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { COMPARE_CHANGE_PREFIX, comparePinTone } from "./versionChanges";

import {
  AskPunttArtworkViewer,
  type HumanCommentSubmitInput,
  type useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { LinkedArtworkViewProvider } from "../../components/ask-puntt-review/linkedArtworkView";
import { type mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useAskPunttReview>["data"]>["review"]
>;

type ArtworkFocus = ReturnType<typeof useReviewArtworkFocus>;

type AskV2ReviewCompareLayoutProps = {
  review: Review;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  artworkFiles: ReturnType<typeof mapReviewFilesToAssets>;
  artworkPaneRef: ArtworkFocus["artworkPaneRef"];
  setArtworkHeaderControls: ArtworkFocus["setArtworkHeaderControls"];
  versionPicker: ReactNode;
  headerControls: ReactNode;
  selectedCommentId: string | null;
  commentNumbers: Record<string, number>;
  currentUserId?: string;
  /** A finding's pin was clicked on the open review's artwork. */
  onOpenComment: (commentId: string) => void;
  onArtworkBackgroundClick: () => void;
  onSubmitHumanComment?: (input: HumanCommentSubmitInput) => Promise<void>;
};

/**
 * The artwork column under its toolbar: the open review's viewer, and while
 * comparing, the compare status strip and the compared version beside it.
 */
export function AskV2ReviewCompareLayout({
  review,
  compare,
  artworkFiles,
  artworkPaneRef,
  setArtworkHeaderControls,
  versionPicker,
  headerControls,
  selectedCommentId,
  commentNumbers,
  currentUserId,
  onOpenComment,
  onArtworkBackgroundClick,
  onSubmitHumanComment,
}: AskV2ReviewCompareLayoutProps) {
  const { t } = useTranslation("ui");
  const {
    isComparing,
    compareReviewId,
    comparison,
    changeAt,
    steppedRegion,
    steppedPin,
    selectedComment,
    versionMark,
  } = compare;

  return (
    <>
      {isComparing && compare.comparedQuery.data ? (
        <AskV2ReviewCompareStatus
          comparison={comparison}
          changeAt={changeAt}
          steppedRegion={steppedRegion}
          hasSelectedComment={
            selectedComment != null && compare.canPlaceFinding
          }
          selectedRegion={compare.selectedRegion}
          pinsShown={compare.pinsShown}
          onStep={compare.stepChange}
          onShowPins={compare.showPins}
        />
      ) : null}
      <LinkedArtworkViewProvider enabled={isComparing}>
        <div className="flex min-h-0 flex-1">
          {isComparing && compareReviewId ? (
            <AskV2ReviewCompare
              reviewId={compareReviewId}
              options={compare.compareOptions}
              onSelect={compare.setCompareChoice}
              selectedComment={steppedPin ?? selectedComment}
              selectedNumber={
                steppedRegion
                  ? steppedRegion.number
                  : selectedCommentId
                    ? commentNumbers[selectedCommentId]
                    : undefined
              }
              currentUserId={currentUserId}
              toolbar={headerControls}
              mark={versionMark(compareReviewId)}
              side={compare.comparedSide}
              changes={compare.comparedChanges}
              // Only a changed window answers here: this version's own
              // findings are not the open review's, and have no card.
              onPinClick={compare.stepToPin}
            />
          ) : null}
          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col"
            data-testid="ask-v2-review-artwork-current"
          >
            {isComparing ? (
              <div
                className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-2"
                data-testid="ask-v2-review-compare-current-header"
              >
                {versionPicker}
                <Button
                  variant="outline"
                  color="gray"
                  size="1"
                  className="ml-auto"
                  data-testid="ask-v2-review-compare-close"
                  onClick={() => compare.setComparing(false)}
                >
                  {t("askV2.review.compareCloseLabel")}
                </Button>
              </div>
            ) : null}
            <div className="min-h-0 flex-1">
              <AskPunttArtworkViewer
                ref={artworkPaneRef}
                reviewId={review.reviewId}
                title={review.title}
                files={artworkFiles}
                comments={compare.viewerComments}
                commentNumbers={compare.viewerNumbers}
                currentUserId={currentUserId}
                activeCommentId={steppedPin?._id ?? selectedCommentId}
                pinTone={comparePinTone(compare.openSide)}
                onHeaderControlsChange={setArtworkHeaderControls}
                onCommentClick={(comment) => {
                  // A changed window is not a finding: clicking its pin
                  // steps to it, and there is nothing to open in the column.
                  if (comment._id.startsWith(COMPARE_CHANGE_PREFIX)) {
                    compare.stepToPin(comment);
                    return;
                  }
                  onOpenComment(comment._id);
                }}
                onArtworkBackgroundClick={onArtworkBackgroundClick}
                // why: same as the judge controls — this composer POSTs
                // `createReviewComment` off the transport. Without the
                // handler the viewer falls back to the read-only placement
                // trigger and a bare click no longer drops a draft pin.
                onSubmitHumanComment={onSubmitHumanComment}
              />
            </div>
          </div>
        </div>
      </LinkedArtworkViewProvider>
    </>
  );
}
